import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { MatchPhone } from "refsix-js-models";

import { RootState } from "../../redux/store";
import {
    LegendVM,
    MatchRibbonsPublic,
    RibbonVM,
} from "../../pages/venue/ribbonTypes";
import { VenueMetric } from "../../pages/venue/leaderboardModel";
import {
    canViewVenueRecords,
    matchEpoch,
    VENUE_WINDOW_DAYS_DEFAULT,
    venueRecordsRoute,
} from "../../pages/venue/venueHistory";
import { LeaderboardTapContext } from "../../pages/venue/leaderboardAnalytics";
import { getMatchRibbons } from "../../services/venueService";
import { isEligible } from "../../services/leaderboardOptInService";
import { useVenueBoard } from "./useVenueBoard";
import { load, ribbonsKey, useVenueStoreVersion } from "./venueStore";

/**
 * Everything the match result page needs to render its venue-leaderboard section.
 *
 * The ribbons and the Local Legend entry are resolved by refauth against the same entries and
 * board snapshot the leaderboard shows, so a ribbon can never claim something the board
 * contradicts. Nothing here works out a tier; it fetches them and hands them to the renderers.
 * The board is still loaded for the venue name, and so that tapping through to the venue page is
 * served from the cache.
 *
 * **The section renders only for a match carrying a `venueRef`** (SCORZA-739 plan §5.2): that tag
 * is the venue the server filed the entry under. A match that has never been submitted renders
 * nothing until it is — `Stats` submits an untagged finished match the first time the result is
 * viewed, which is also how historic matches pick up their ribbons.
 *
 * An opted-out user has no entries, so the server has nothing to resolve; the Legend slot shows
 * its `notOptedIn` invitation from here instead.
 */
export interface VenueRibbons {
    /** False when this match cannot resolve a venue at all; render nothing. */
    available: boolean;
    ribbon: (metric: VenueMetric) => RibbonVM | null;
    legend: LegendVM | null;
    venueName: string | null;
    venueRoute: string;
    analytics: LeaderboardTapContext;
}

function useMatchRibbons(
    venueId: string | null,
    matchId: string | undefined
): MatchRibbonsPublic | null {
    const [ribbons, setRibbons] = useState<MatchRibbonsPublic | null>(null);
    // An invalidation after one of my submissions drops these keys; re-run to pick that up.
    const storeVersion = useVenueStoreVersion();

    useEffect(() => {
        if (!venueId || !matchId) {
            setRibbons(null);
            return;
        }
        let cancelled = false;
        load(ribbonsKey(venueId, matchId), () =>
            getMatchRibbons(venueId, matchId)
        )
            .then((value) => {
                if (!cancelled) {
                    setRibbons(value);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    Sentry.captureException(error);
                    setRibbons(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [venueId, matchId, storeVersion]);

    return ribbons;
}

export function useVenueRibbons(match: MatchPhone): VenueRibbons {
    const profile = useSelector(
        (state: RootState) => state.currentUser?.profile
    );
    const onBoards = isEligible(profile);

    // `canViewVenueRecords` still gates the venue page link (a deep link can resolve by
    // proximity); the ribbons need the tag.
    const venueId = match.venueRef?.venueId;
    const available = canViewVenueRecords(match) && !!venueId;
    const { venue, board } = useVenueBoard({
        venueId: available ? venueId : null,
    });
    const resolved = useMatchRibbons(
        available && onBoards ? venueId! : null,
        match._id
    );

    const venueName = venue?.name || match.venue || null;

    return useMemo(() => {
        const analytics: LeaderboardTapContext = {
            venueId,
            venueName,
            matchId: match._id,
            matchEpoch: matchEpoch(match),
        };

        if (!available) {
            return {
                available: false,
                ribbon: () => null,
                legend: null,
                venueName,
                venueRoute: "",
                analytics,
            };
        }

        const ribbon = (metric: VenueMetric): RibbonVM | null =>
            resolved?.ribbons[metric] ?? null;

        const legend: LegendVM | null = onBoards
            ? resolved?.legend ?? null
            : {
                  tier: "notOptedIn",
                  matchesPlayed: 0,
                  windowDays: board?.windowDays ?? VENUE_WINDOW_DAYS_DEFAULT,
                  leaderName: board?.localLegend?.displayName,
              };

        return {
            available: true,
            ribbon,
            legend,
            venueName,
            venueRoute: venueRecordsRoute(match),
            analytics,
        };
    }, [available, board, match, onBoards, resolved, venueId, venueName]);
}
